import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Camera, ShoppingBag, Share2, ArrowRight, Check, Loader2, Rocket } from "lucide-react";
import { toast } from "sonner";
import { vpAxios } from "./api";
import useVaipedeManifest from "./useVaipedeManifest";

/**
 * Wizard pós-cadastro VAI PEDE (Out/2026).
 * 3 passos opcionais que ajudam o lojista a sair "do zero" com o cardápio digital.
 * O usuário pode pular qualquer passo e cair direto no dashboard.
 */
export default function VaiPedeBoasVindasPage() {
  useVaipedeManifest();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [step, setStep] = useState(1);
  const [savingLogo, setSavingLogo] = useState(false);
  const [savingProd, setSavingProd] = useState(false);
  const [prod, setProd] = useState({ nome: "", preco: "", descricao: "" });
  const [cardapioUrl, setCardapioUrl] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await vpAxios.get("/vaipede/auth/me");
        setMe(data);
        setCardapioUrl(`${window.location.origin}/vaipede/cardapio/${data.slug}`);
      } catch {
        navigate("/vaipede/login");
      }
    })();
  }, [navigate]);

  // Comprime a imagem no cliente antes de enviar: redimensiona para no máx 800px
  // de largura e converte para JPEG qualidade 0.85. Logo não precisa ser gigante.
  const comprimirImagem = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 800;
        const scale = Math.min(1, MAX_W / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, w, h); // fundo branco para PNG transparente
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const subirLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Selecione uma imagem"); return; }
    setSavingLogo(true);
    try {
      const dataUrl = await comprimirImagem(file);
      // Faz upload (backend aceita data_url JSON e devolve URL servida do banco)
      const { data: img } = await vpAxios.post("/vaipede/uploads/imagem", { data_url: dataUrl });
      // Salva no config da loja
      await vpAxios.put("/vaipede/loja/config", { logo_url: img.url });
      toast.success("Logo salva!");
      setStep(2);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Falha ao salvar logo";
      toast.error(typeof msg === "string" ? msg : "Falha ao salvar logo");
    } finally { setSavingLogo(false); }
  };

  const criarPrimeiroProduto = async (e) => {
    e.preventDefault();
    if (!prod.nome || !prod.preco) { toast.error("Nome e preço são obrigatórios"); return; }
    setSavingProd(true);
    try {
      await vpAxios.post("/vaipede/produtos", {
        nome: prod.nome,
        preco: Number(prod.preco),
        descricao: prod.descricao,
        ativo: true,
      });
      toast.success("Produto criado!");
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar produto");
    } finally { setSavingProd(false); }
  };

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(cardapioUrl);
      toast.success("Link copiado!");
    } catch { toast.info(cardapioUrl); }
  };

  const compartilharWhats = () => {
    const msg = encodeURIComponent(
      `🍔 *${me?.loja_nome || "Minha loja"}* está no VAI PEDE!\n\nPeça pelo cardápio digital:\n${cardapioUrl}`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const concluir = () => {
    toast.success("Pronto! Bem-vindo ao VAI PEDE 🚀");
    navigate("/vaipede/dashboard");
  };

  const steps = [
    { n: 1, icon: Camera, title: "Logo da loja" },
    { n: 2, icon: ShoppingBag, title: "Primeiro produto" },
    { n: 3, icon: Share2, title: "Compartilhar cardápio" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-red-50/30" data-testid="vaipede-boas-vindas">
      <nav className="border-b border-neutral-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl grid place-items-center bg-[#EA1D2C] text-white font-black">V</div>
            <span className="font-black text-xl tracking-tight">VAI <span className="text-[#EA1D2C]">PEDE</span></span>
          </div>
          <button onClick={concluir} className="text-sm font-bold text-neutral-500 hover:text-neutral-900" data-testid="btn-pular-wizard">
            Pular e ir ao painel →
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Header + progresso */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-black mb-4">
            <Rocket className="w-3.5 h-3.5"/> Bem-vindo, {me?.nome?.split(" ")[0] || "lojista"}!
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
            Sua conta está pronta.
          </h1>
          <p className="text-neutral-600 mt-2 text-base">
            Em menos de 2 minutos, deixe sua loja no ar com um cardápio incrível.
          </p>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-1 sm:gap-3 mb-10">
          {steps.map((s, i) => {
            const done = step > s.n;
            const current = step === s.n;
            const Icon = s.icon;
            return (
              <div key={s.n} className="flex items-center gap-1 sm:gap-3">
                <div className={`w-10 h-10 rounded-full grid place-items-center font-black text-sm transition ${
                  done ? "bg-emerald-500 text-white" : current ? "bg-[#EA1D2C] text-white shadow-lg shadow-red-500/30 scale-110" : "bg-white text-neutral-400 border-2 border-neutral-200"
                }`}>
                  {done ? <Check className="w-5 h-5"/> : <Icon className="w-5 h-5"/>}
                </div>
                <span className={`hidden sm:inline text-sm font-bold ${current ? "text-neutral-900" : done ? "text-emerald-700" : "text-neutral-400"}`}>
                  {s.title}
                </span>
                {i < steps.length - 1 && <div className={`w-6 sm:w-10 h-0.5 ${done ? "bg-emerald-500" : "bg-neutral-200"}`}/>}
              </div>
            );
          })}
        </div>

        {/* Step 1 — Logo */}
        {step === 1 && (
          <div className="bg-white rounded-3xl border border-neutral-200 p-8 shadow-sm max-w-xl mx-auto" data-testid="wizard-step-logo">
            <div className="w-14 h-14 rounded-2xl bg-red-50 text-[#EA1D2C] grid place-items-center mb-4">
              <Camera className="w-7 h-7"/>
            </div>
            <h2 className="text-2xl font-black">Suba a logo da loja</h2>
            <p className="text-neutral-500 text-sm mt-1 mb-5">
              Isso aparece no topo do cardápio digital e nos PDFs dos pedidos.
            </p>
            <label className="block border-2 border-dashed border-neutral-300 rounded-2xl p-8 text-center cursor-pointer hover:border-[#EA1D2C] hover:bg-red-50/40 transition" data-testid="wizard-upload-logo">
              <input type="file" accept="image/*" className="hidden" onChange={e => subirLogo(e.target.files?.[0])} disabled={savingLogo}/>
              {savingLogo ? (
                <div className="flex items-center justify-center gap-2 text-neutral-600"><Loader2 className="w-5 h-5 animate-spin"/> Enviando...</div>
              ) : (
                <>
                  <Camera className="w-10 h-10 mx-auto text-neutral-400 mb-2"/>
                  <div className="font-bold">Clique para escolher uma imagem</div>
                  <div className="text-xs text-neutral-500 mt-1">PNG ou JPG · redimensionamos automaticamente</div>
                </>
              )}
            </label>
            <div className="flex items-center justify-between mt-4">
              <button onClick={() => setStep(2)} className="text-sm text-neutral-500 font-bold hover:text-neutral-900">Pular por agora</button>
              <span className="text-xs text-neutral-400">Passo 1 de 3</span>
            </div>
          </div>
        )}

        {/* Step 2 — Primeiro produto */}
        {step === 2 && (
          <form onSubmit={criarPrimeiroProduto} className="bg-white rounded-3xl border border-neutral-200 p-8 shadow-sm max-w-xl mx-auto" data-testid="wizard-step-produto">
            <div className="w-14 h-14 rounded-2xl bg-red-50 text-[#EA1D2C] grid place-items-center mb-4">
              <ShoppingBag className="w-7 h-7"/>
            </div>
            <h2 className="text-2xl font-black">Cadastre seu primeiro produto</h2>
            <p className="text-neutral-500 text-sm mt-1 mb-5">
              Comece com o item mais vendido. Depois você adiciona o resto no painel.
            </p>
            <div className="space-y-3">
              <input required value={prod.nome} onChange={e => setProd({...prod, nome: e.target.value})} placeholder="Nome do produto (ex: X-Burguer)" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#EA1D2C] outline-none" data-testid="wizard-prod-nome"/>
              <input required type="number" step="0.01" value={prod.preco} onChange={e => setProd({...prod, preco: e.target.value})} placeholder="Preço (ex: 24.90)" className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#EA1D2C] outline-none" data-testid="wizard-prod-preco"/>
              <textarea value={prod.descricao} onChange={e => setProd({...prod, descricao: e.target.value})} placeholder="Descrição (opcional)" rows={2} className="w-full px-4 py-3 rounded-xl border border-neutral-200 focus:border-[#EA1D2C] outline-none resize-none" data-testid="wizard-prod-descricao"/>
            </div>
            <button type="submit" disabled={savingProd} className="w-full mt-5 py-4 rounded-xl bg-[#EA1D2C] text-white font-black text-base hover:bg-[#c8121f] disabled:opacity-60 flex items-center justify-center gap-2 shadow-lg shadow-red-500/20" data-testid="wizard-prod-criar">
              {savingProd && <Loader2 className="w-4 h-4 animate-spin"/>}
              Criar produto <ArrowRight className="w-4 h-4"/>
            </button>
            <div className="flex items-center justify-between mt-4">
              <button type="button" onClick={() => setStep(3)} className="text-sm text-neutral-500 font-bold hover:text-neutral-900">Pular por agora</button>
              <span className="text-xs text-neutral-400">Passo 2 de 3</span>
            </div>
          </form>
        )}

        {/* Step 3 — Compartilhar */}
        {step === 3 && (
          <div className="bg-white rounded-3xl border border-neutral-200 p-8 shadow-sm max-w-xl mx-auto" data-testid="wizard-step-share">
            <div className="w-14 h-14 rounded-2xl bg-red-50 text-[#EA1D2C] grid place-items-center mb-4">
              <Share2 className="w-7 h-7"/>
            </div>
            <h2 className="text-2xl font-black">Compartilhe com seus clientes</h2>
            <p className="text-neutral-500 text-sm mt-1 mb-5">
              Este é o link do seu cardápio digital. Mande no WhatsApp, cole no bio do Instagram, imprima no cardápio físico.
            </p>
            <div className="rounded-xl bg-neutral-50 border border-neutral-200 p-3 mb-4 flex items-center gap-2">
              <Store className="w-4 h-4 text-[#EA1D2C] shrink-0"/>
              <code className="text-xs sm:text-sm font-mono flex-1 truncate" data-testid="wizard-cardapio-url">{cardapioUrl}</code>
              <button onClick={copiarLink} className="px-3 py-1.5 rounded-lg bg-neutral-900 text-white text-xs font-bold hover:bg-black" data-testid="wizard-copy-link">
                Copiar
              </button>
            </div>
            <button onClick={compartilharWhats} className="w-full py-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-black text-base flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 mb-3" data-testid="wizard-share-whats">
              <Share2 className="w-5 h-5"/> Compartilhar no WhatsApp
            </button>
            <button onClick={concluir} className="w-full py-4 rounded-xl bg-neutral-900 hover:bg-black text-white font-black text-base flex items-center justify-center gap-2" data-testid="wizard-concluir">
              Ir para o meu painel <ArrowRight className="w-5 h-5"/>
            </button>
            <div className="text-center mt-4">
              <span className="text-xs text-neutral-400">Passo 3 de 3 · Tudo pronto!</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
